import { Navigate, NavLink, Outlet, useLocation } from 'react-router-dom'
import { usePageAccess } from '@/hooks/usePageAccess'
import { usePendingReviewStore } from '@/store/pendingReviewStore'

const tabs = [
  { to: '/bookkeeper/doc-review', label: 'Doc review' },
  { to: '/bookkeeper/email-rules', label: 'Email rules' },
  { to: '/bookkeeper/email-search', label: 'Email search' },
  { to: '/bookkeeper/settings', label: 'Settings', requiresEdit: true },
] as const

export default function BookkeeperLayout() {
  const { canEdit } = usePageAccess('invoices')
  const location = useLocation()
  const hasPendingReview = (usePendingReviewStore((s) => s.count) ?? 0) > 0

  const visibleTabs = tabs.filter((tab) => !('requiresEdit' in tab && tab.requiresEdit) || canEdit)

  if (!canEdit && location.pathname.startsWith('/bookkeeper/settings')) {
    return <Navigate to="/bookkeeper/doc-review" replace />
  }

  const isDocReview = location.pathname === '/bookkeeper/doc-review'

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="bg-white border-b border-gray-200 shrink-0">
        <div className="px-4 lg:px-6 py-3">
          <h1 className="text-xl font-semibold text-gray-900">Invoices</h1>
          <p className="mt-0.5 text-sm text-gray-500">
            Review invoices, manage email rules, and configure QuickBooks settings
          </p>
        </div>

        <div className="border-t border-gray-200 px-4 lg:px-6 py-4">
          <div className="overflow-x-auto">
            <div
              className="inline-flex gap-2 bg-gray-200 p-1 rounded-lg min-w-max"
              role="tablist"
              aria-label="Invoices sections"
            >
              {visibleTabs.map((tab) => (
                <NavLink
                  key={tab.to}
                  to={tab.to}
                  role="tab"
                  className={({ isActive }) =>
                    `px-4 py-1.5 rounded-md text-sm font-medium transition-all whitespace-nowrap inline-flex items-center gap-1.5 ${
                      isActive
                        ? 'bg-white text-brand-700 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`
                  }
                >
                  {tab.label}
                  {tab.to === '/bookkeeper/doc-review' && hasPendingReview && (
                    <span
                      className="h-2 w-2 rounded-full bg-red-500 shrink-0"
                      aria-label="Documents awaiting review"
                      title="Documents awaiting review"
                    />
                  )}
                </NavLink>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div
        className={`flex min-h-0 flex-1 flex-col px-3 py-3 sm:px-6 sm:py-4 lg:px-8 ${
          isDocReview ? 'overflow-hidden' : 'overflow-y-auto'
        }`}
      >
        {isDocReview ? (
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            <Outlet />
          </div>
        ) : (
          <Outlet />
        )}
      </div>
    </div>
  )
}
